import { Injectable, inject, signal, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { RegistrationData } from '@app/core/types/registration-data.types';
import { RegistrationDataService } from '@app/core/services/registration-data.service';
import { InvitationVerification } from '@app/invitation/invitation-verification.model';
import { StorageVariables } from '@app/shared/enums/storage.enums';

@Injectable({ providedIn: 'root' })
export class InvitationService {
  http = inject(HttpClient);
  registrationDataService = inject(RegistrationDataService);

  readonly verifiedInvitation = signal<InvitationVerification | null>(null);
  private readonly _invitationCode = signal<string | null>(localStorage.getItem(StorageVariables.InvitationCode));

  set invitationCode(invitationCode: string) {
    this._invitationCode.set(invitationCode);
    localStorage.setItem(StorageVariables.InvitationCode, invitationCode);
  }

  get invitationCode(): Signal<string | null> {
    return this._invitationCode;
  }

  prefillRegistrationData(invitationId: string): Observable<RegistrationData | undefined> {
    return this.getInvitation(invitationId).pipe(
      tap((data: RegistrationData) => this.registrationDataService.setRegistrationData(data)),
      catchError(() => of(undefined))
    );
  }

  clearVerifiedInvitation(): void {
    this.verifiedInvitation.set(null);
  }

  clearInvitationContext(): void {
    this.registrationDataService.clearRegistrationData();
    this.clearVerifiedInvitation();
    this.invitationCode = null;
    localStorage.removeItem(StorageVariables.InvitationId);
    localStorage.removeItem(StorageVariables.InvitationCode);
  }

  private getInvitation(invitationId: string): Observable<RegistrationData> {
    return this.http.get<RegistrationData>(`${environment.url.api}/invitation/${invitationId}/user`);
  }

  verifyInvitation(invitationCode: string): Observable<InvitationVerification> {
    return this.http
      .post<InvitationVerification>(`${environment.url.api}/invitation/verify`, { invitationCode })
      .pipe(tap((verifiedInvitation) => this.verifiedInvitation.set(verifiedInvitation)));
  }

  linkUser(invitationId: string): Observable<void> {
    return this.http.post<void>(`${environment.url.api}/invitation/${invitationId}/link`, {});
  }
}
